/**
 * 兌換碼：兩個 realm 的 **API 分流**與路由名分流（批104）。
 *
 * 背景與 `lib/hostingRoutes.ts` 完全相同：`RedeemCampaignsView`／`RedeemCampaignDetailView` 兩支元件由我方
 * 員工與代理商**共用**（清單、代碼、兌換紀錄三張表逐欄相同，差別只在打哪個前綴、看不看得到
 * 內部備註與兌換人 IP），但**路由是兩條**——`router/index.spec.ts` 釘住「代理商只進得了
 * `reseller-*` 的頁」這條結構規則，不能把 `reseller` 塞進我方那條的 `meta.roles`。
 *
 * 所以元件只問兩件事：`mode`（打哪一支 API）與 `pr()`（RouterLink 指向哪一條路由）。
 *
 * ★ `mode` 從 `auth.isReseller` 推導而不是由路由 props 傳：角色是 `GET /me` 載回來的，
 *   而 guard 保證代理商只走得到 `reseller-*`、我方員工只走得到另一條——兩者恆一致。
 *   寫成函式（不是常數）的理由同 `useHostingRoute`：setup 期角色可能還沒載回來。
 *
 * ★ 代理商 realm **沒有**建立與修改（後端根本沒有那兩支端點）——facade 刻意不放這兩個方法，
 *   共用元件要建立活動時自己判斷 `mode === 'admin' && auth.isManagerOrAbove`（批110 下放）。
 */
import * as ls from '@/api/ls'
import type {
  RedeemCampaign,
  RedeemCampaignPage,
  RedeemCode,
  RedeemCodePage,
  RedemptionPage,
} from '@/api/ls'
import * as reseller from '@/api/reseller'
import { useAuthStore } from '@/stores/auth'

export type RedeemMode = 'admin' | 'reseller'

/** 清單過濾：代理商那支只吃 `status`（他的活動必然都是自家的、產品也在自己範圖內）。 */
export interface RedeemListOpts {
  limit?: number
  cursor?: string | null
  status?: string
  product?: string
  kind?: string
  reseller_id?: string
}

export interface RedeemApi {
  list(opts?: RedeemListOpts): Promise<RedeemCampaignPage>
  get(id: string): Promise<RedeemCampaign>
  listCodes(
    id: string,
    opts?: { limit?: number; cursor?: string | null; status?: string },
  ): Promise<RedeemCodePage>
  issueCodes(
    id: string,
    body: { count: number; to_customer_id?: string | null; note?: string | null },
  ): Promise<{ items: RedeemCode[]; campaign: RedeemCampaign }>
  voidCode(id: string, codeId: string): Promise<{ code: RedeemCode; campaign: RedeemCampaign }>
  listRedemptions(
    id: string,
    opts?: { limit?: number; cursor?: string | null; customer_id?: string },
  ): Promise<RedemptionPage>
}

const ADMIN_API: RedeemApi = {
  list: (opts = {}) => ls.listRedeemCampaigns(opts),
  get: (id) => ls.getRedeemCampaign(id),
  listCodes: (id, opts = {}) => ls.listRedeemCodes(id, opts),
  issueCodes: (id, body) => ls.issueRedeemCodes(id, body),
  voidCode: (id, codeId) => ls.voidRedeemCode(id, codeId),
  listRedemptions: (id, opts = {}) => ls.listRedemptions(id, opts),
}

const RESELLER_API: RedeemApi = {
  // 代理商那支不吃 `product`／`kind`／`reseller_id`（多送也只是被忽略，這裡直接不送）。
  list: (opts = {}) =>
    reseller.listRedeemCampaigns({ limit: opts.limit, cursor: opts.cursor, status: opts.status }),
  get: (id) => reseller.getRedeemCampaign(id),
  listCodes: (id, opts = {}) => reseller.listRedeemCodes(id, opts),
  issueCodes: (id, body) => reseller.issueRedeemCodes(id, body),
  voidCode: (id, codeId) => reseller.voidRedeemCode(id, codeId),
  // 代理商的兌換紀錄不收 `customer_id` 過濾（他只有自家客戶，清單本來就已收斂）。
  listRedemptions: (id, opts = {}) =>
    reseller.listRedemptions(id, { limit: opts.limit, cursor: opts.cursor }),
}

/** 純函式版（測試用）。 */
export function redeemApi(mode: RedeemMode): RedeemApi {
  return mode === 'reseller' ? RESELLER_API : ADMIN_API
}

/** 兌換碼路由的我方員工版名稱（代理商版＝前綴 `reseller-`）。 */
export type RedeemRouteName = 'redeem-campaigns' | 'redeem-campaign-detail'
export function redeemRouteName(name: RedeemRouteName, isReseller: boolean): string {
  return isReseller ? `reseller-${name}` : name
}

/** 元件內用：`const { mode, api, pr } = useRedeem()`。 */
export function useRedeem() {
  const auth = useAuthStore()
  return {
    /** 每次取值才問 store（setup 期角色可能還沒載回來）。 */
    mode: (): RedeemMode => (auth.isReseller ? 'reseller' : 'admin'),
    api: (): RedeemApi => redeemApi(auth.isReseller ? 'reseller' : 'admin'),
    pr: (name: RedeemRouteName): string => redeemRouteName(name, auth.isReseller),
  }
}
